import { useCallback, useState } from "react";
import { deleteScheduleBlock } from "../../../../../services/api/schedule";
import { getAspectBlockId } from "../../model/scheduleBlockSelectors";
import { sameScheduleBlockId } from "../../model/scheduleIds";

/**
 * Manage delete-scope confirmation modal and server/local delete flows.
 *
 * @param {Object} params
 * @param {Function} params.setBlocks
 * @param {Function} params.setWorkScopeAspects
 * @param {Function} params.runPlanningOperation PlanningContext mutation wrapper.
 * @returns {{
 *   deleteScopeBlockModal: Object|null,
 *   deleteScopeBlockBusy: boolean,
 *   requestDeleteScopeBlock: (block: Object) => void,
 *   closeDeleteScopeBlockModal: () => void,
 *   confirmDeleteScopeBlock: () => Promise<void>,
 * }}
 */
export function useScheduleDeleteScopeBlock({
  setBlocks,
  setWorkScopeAspects,
  runPlanningOperation,
}) {
  const [deleteScopeBlockModal, setDeleteScopeBlockModal] = useState(null);
  const [deleteScopeBlockBusy, setDeleteScopeBlockBusy] = useState(false);

  /** Open delete-scope confirmation modal for the selected block. */
  const requestDeleteScopeBlock = useCallback((block) => {
    setDeleteScopeBlockModal(block ?? null);
  }, []);

  /** Close delete confirmation modal unless a delete request is currently in flight. */
  const closeDeleteScopeBlockModal = useCallback(() => {
    if (!deleteScopeBlockBusy) {
      setDeleteScopeBlockModal(null);
    }
  }, [deleteScopeBlockBusy]);

  /** Delete selected scope block (or remove optimistic local block) and reconcile linked aspects. */
  const confirmDeleteScopeBlock = useCallback(
    async () => {
      const block = deleteScopeBlockModal;
      if (!block) {
        return;
      }
      const blockId = block.id;
      const idStr = String(blockId);

      if (idStr.startsWith("block-local-")) {
        setBlocks((prev) => prev.filter((b) => !sameScheduleBlockId(b.id, blockId)));
        setWorkScopeAspects((prev) =>
          prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId))
        );
        setDeleteScopeBlockModal(null);
        return;
      }

      if (!/^\d+$/.test(idStr)) {
        setDeleteScopeBlockModal(null);
        return;
      }

      setDeleteScopeBlockBusy(true);
      try {
        await runPlanningOperation({
          module: "schedule",
          operation: "deleteScheduleBlock",
          resources: ["schedule.blocks", "schedule.aspects"],
          apiCall: () => deleteScheduleBlock(blockId),
        });
        setBlocks((prev) => prev.filter((b) => !sameScheduleBlockId(b.id, blockId)));
        setWorkScopeAspects((prev) =>
          prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId))
        );
        setDeleteScopeBlockModal(null);
      } catch (err) {
        console.error("Schedule block delete failed", err);
      } finally {
        setDeleteScopeBlockBusy(false);
      }
    },
    [
      deleteScopeBlockModal,
      runPlanningOperation,
      setBlocks,
      setWorkScopeAspects,
    ]
  );

  return {
    deleteScopeBlockModal,
    deleteScopeBlockBusy,
    requestDeleteScopeBlock,
    closeDeleteScopeBlockModal,
    confirmDeleteScopeBlock,
  };
}

